<template>
  <el-card
    v-loading="isLoading"
    shadow="never"
  >
    <template v-if="anteMeridiemEvents.length > 0 || postMeridiemEvents.length > 0">
      <el-collapse
        :value="[
          (anteMeridiemEvents.length > 0 ? 'morning' : undefined),
          (postMeridiemEvents.length > 0 ? 'afternoon' : undefined)
        ]"
      >
        <el-collapse-item
          name="past-due"
          :disabled="pastDueEvents.length < 1"
        >
          <template #title>
            Past Due

            <el-tag
              v-if="pastDueEvents.total > 0"
              size="mini"
              type="info"
              class="ml-2"
            >
              {{ pastDueEvents.total }}
            </el-tag>
          </template>

          <EventsTable
            :events="pastDueEvents"
            @completed-event="reload"
            @deferred-event="reload"
            @created-event="reload"
          />

          <el-pagination
            class="text-center pt-3"
            :current-page.sync="pastDueEvents.page"
            :page-size.sync="pastDueEvents.limit"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pastDueEvents.total"
            @size-change="reload"
            @current-change="reload"
          />
        </el-collapse-item>

        <el-collapse-item
          name="morning"
          :disabled="anteMeridiemEvents.length < 1"
        >
          <template slot="title">
            {{ isPostMeridiem ? 'Earlier Today' : 'This Morning' }}

            <el-tag
              v-if="anteMeridiemEvents.total > 0"
              size="mini"
              type="info"
              class="ml-2"
            >
              {{ anteMeridiemEvents.total }}
            </el-tag>
          </template>

          <EventsTable
            :events="anteMeridiemEvents"
            @completed-event="reload"
            @deferred-event="reload"
            @created-event="reload"
          />
        </el-collapse-item>

        <el-collapse-item
          name="afternoon"
          :disabled="postMeridiemEvents.length < 1"
        >
          <template slot="title">
            {{ isAnteMeridiem ? 'Later Today' : 'This Afternoon' }}

            <el-tag
              v-if="postMeridiemEvents.total > 0"
              size="mini"
              type="info"
              class="ml-2"
            >
              {{ postMeridiemEvents.total }}
            </el-tag>
          </template>

          <EventsTable
            :events="postMeridiemEvents"
            @completed-event="reload"
            @deferred-event="reload"
            @created-event="reload"
          />
        </el-collapse-item>

        <el-collapse-item
          name="upcoming"
          :disabled="upcomingEvents.length < 1"
        >
          <template slot="title">
            Upcoming

            <el-tag
              v-if="upcomingEvents.total > 0"
              size="mini"
              type="info"
              class="ml-2"
            >
              {{ upcomingEvents.total }}
            </el-tag>
          </template>

          <EventsTable
            :events="upcomingEvents"
            @completed-event="reload"
            @deferred-event="reload"
            @created-event="reload"
          />

          <el-pagination
            class="text-center pt-3"
            :current-page.sync="upcomingEvents.page"
            :page-size.sync="upcomingEvents.limit"
            layout="total, sizes, prev, pager, next, jumper"
            :total="upcomingEvents.total"
            @size-change="reload"
            @current-change="reload"
          />
        </el-collapse-item>
      </el-collapse>
    </template>

    <p
      v-else
      class="w-100 p-3 text-muted text-center"
    >
      <FontAwesomeIcon
        :icon="[ 'fal', 'sunglasses' ]"
        size="4x"
        fixed-width
        class="pb-3"
      /><br>
      You've Got Nothing on Today
    </p>
  </el-card>
</template>

<script>
import EventsTable from '@/components/Application/Sales/Events/Table'
import Filter, { IS_BETWEEN, IS_LESS_THAN, IS } from '@/plugins/sdk/Filter'
import { moment } from '@/plugins/moment'

const NOW = moment()
const TODAY_ANTE_MERIDIEM = moment(NOW).set({ hour: 11, minute: 59, second: 59 })
const TODAY_POST_MERIDIEM = moment(NOW).hour(12)
const TODAY_START_OF_DAY = moment(NOW).startOf('day')
const TODAY_END_OF_DAY = moment(NOW).endOf('day')
const TOMORROW = moment(NOW).nextBusinessDay().startOf('day')
const TOMORROW_END_OF_DAY = moment(TOMORROW).endOf('day')

export default {
  components: {
    EventsTable
  },
  data () {
    return {
      isLoadingPastDueEvents: false,
      isLoadingAnteMeridiemEvents: false,
      isLoadingPostMeridiemEvents: false,
      isLoadingUpcomingEvents: false,
      portfolios: [],
      pastDueEvents: [],
      anteMeridiemEvents: [],
      postMeridiemEvents: [],
      upcomingEvents: []
    }
  },
  computed: {
    isLoading () {
      return this.isLoadingPastDueEvents ||
        this.isLoadingAnteMeridiemEvents ||
        this.isLoadingPostMeridiemEvents ||
        this.isLoadingUpcomingEvents
    },
    isAnteMeridiem () {
      return this.$moment().format('HH') < 12
    },
    isPostMeridiem () {
      return !this.isAnteMeridiem
    },
    pastDueEventsFilters () {
      const filters = [
        new Filter('due_at', IS_LESS_THAN, TODAY_START_OF_DAY)
      ]

      if (this.portfolios.length) {
        filters.push(new Filter('account.portfolio', IS, this.portfolios))
      }

      return filters
    },
    anteMeridiemEventsFilters () {
      const filters = [
        new Filter('due_at', IS_BETWEEN, [ TODAY_START_OF_DAY, TODAY_ANTE_MERIDIEM ])
      ]

      if (this.portfolios.length) {
        filters.push(new Filter('account.portfolio', IS, this.portfolios))
      }

      return filters
    },
    postMeridiemEventsFilters () {
      const filters = [
        new Filter('due_at', IS_BETWEEN, [ TODAY_POST_MERIDIEM, TODAY_END_OF_DAY ])
      ]

      if (this.portfolios.length) {
        filters.push(new Filter('account.portfolio', IS, this.portfolios))
      }

      return filters
    },
    upcomingEventsFilters () {
      const filters = [
        new Filter('due_at', IS_BETWEEN, [ TODAY_END_OF_DAY, TOMORROW_END_OF_DAY ]),
        new Filter('completed_at', IS, 'null', true, true)
      ]

      if (this.portfolios.length) {
        filters.push(new Filter('account.portfolio', IS, this.portfolios))
      }

      return filters
    }
  },
  created () {
    this
      .$sdk
      .session
      .read(undefined, builder => {
        builder.include = [ 'profile.portfolios' ]
      })
      .then(session => {
        this.portfolios = this.$_.map(session.profile.portfolios, 'id')

        Promise
          .all([
            this.getPastDueEvents(),
            this.getAnteMeridiemEvents(),
            this.getPostMeridiemEvents(),
            this.getUpcomingEvents()
          ])
          .then(results => {
            this.pastDueEvents = results[0]
            this.anteMeridiemEvents = results[1]
            this.postMeridiemEvents = results[2]
            this.upcomingEvents = results[3]
          })
      })
  },
  methods: {
    async getPastDueEvents () {
      this.isLoadingPastDueEvents = true

      const events = await this
        .$sdk
        .account_event
        .list(request => {
          request.filters = this.pastDueEventsFilters
          request.include = ['type', 'account.portfolio', 'account.lead']
          request.limit = 10
          request.order = 'priority:desc'
        })

      this.isLoadingPastDueEvents = false

      return events
    },
    async getAnteMeridiemEvents () {
      this.isLoadingAnteMeridiemEvents = true

      const events = await this
        .$sdk
        .account_event
        .list(request => {
          request.filters = this.anteMeridiemEventsFilters
          request.include = ['type', 'account.portfolio', 'account.lead']
          request.limit = 1000
          request.order = 'priority:desc'
        })

      this.isLoadingAnteMeridiemEvents = false

      return events
    },
    async getPostMeridiemEvents () {
      this.isLoadingPostMeridiemEvents = true

      const events = await this
        .$sdk
        .account_event
        .list(request => {
          request.filters = this.postMeridiemEventsFilters
          request.include = ['type', 'account.portfolio', 'account.lead']
          request.limit = 1000
          request.order = 'priority:desc'
        })

      this.isLoadingPostMeridiemEvents = false

      return events
    },
    async getUpcomingEvents () {
      this.isLoadingUpcomingEvents = true

      const events = await this
        .$sdk
        .account_event
        .list(request => {
          request.filters = this.upcomingEventsFilters
          request.include = ['type', 'account.portfolio', 'account.lead']
          request.limit = 10
          request.order = 'priority:desc'
        })

      this.isLoadingUpcomingEvents = false

      return events
    },
    async reload () {
      try {
        if (!this.loading) {
          await Promise.all([
            this.reloadPastDueEvents(),
            this.reloadAnteMeridiemEvents(),
            this.reloadPostMeridiemEvents(),
            this.reloadUpcomingEvents()
          ])
        }
      } catch {
        this.$message.error('Failed to load Events')
      }
    },
    async reloadPastDueEvents () {
      this.isLoadingPastDueEvents = true

      await this.pastDueEvents.reload(request => { request.filters = this.pastDueEventsFilters })

      this.isLoadingPastDueEvents = false
    },
    async reloadAnteMeridiemEvents () {
      this.isLoadingAnteMeridiemEvents = true

      await this.anteMeridiemEvents.reload(request => { request.filters = this.anteMeridiemEventsFilters })

      this.isLoadingAnteMeridiemEvents = false
    },
    async reloadPostMeridiemEvents () {
      this.isLoadingPostMeridiemEvents = true

      await this.postMeridiemEvents.reload(request => { request.filters = this.postMeridiemEventsFilters })

      this.isLoadingPostMeridiemEvents = false
    },
    async reloadUpcomingEvents () {
      this.isLoadingUpcomingEvents = true

      await this.upcomingEvents.reload(request => { request.filters = this.upcomingEventsFilters })

      this.isLoadingUpcomingEvents = false
    }
  }
}
</script>
